import { useState, useEffect } from 'react';
import counselorServices from 'services/counselorServices';
import { useAppSelector } from 'app/hooks';

interface CounselorInfo {
  id: number;
  name: string;
  email?: string;
  avatar?: string;
}

interface UseCounselorReturn {
  counselor: CounselorInfo | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useCounselor = (): UseCounselorReturn => {
  const [counselor, setCounselor] = useState<CounselorInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const basicInformation = useAppSelector((store) => store.basicInformation.myBasicInformation);
  const userRole = basicInformation?.user_role ?? '';

  const fetchCounselor = async () => {
    if (!basicInformation?.student_id || userRole !== 'student') {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get counselor information for the student
      const counselorData = await counselorServices.getCounselors(basicInformation.student_id);
      
      if (counselorData && counselorData.length > 0) {
        // Get the first approved counselor
        const assignedCounselor = counselorData[0];
        
        // Get detailed counselor information
        const counselorDetail: any = await counselorServices.getPartnerById(assignedCounselor.partner_id);
        
        setCounselor({
          id: counselorDetail?.id || assignedCounselor.partner_id,
          name: counselorDetail?.first_name && counselorDetail?.last_name 
            ? `${counselorDetail.first_name} ${counselorDetail.last_name}`.trim()
            : counselorDetail?.name || 'Counselor',
          email: counselorDetail?.email,
          avatar: counselorDetail?.profile_picture || counselorDetail?.avatar
        });
      } else {
        // If no counselor assigned, use default values
        setCounselor({
          id: 1,
          name: 'Counselor',
          email: undefined,
          avatar: undefined
        });
      }
    } catch (err) {
      console.error('Error fetching counselor:', err);
      setError('Failed to load counselor information');
      // Fallback to default counselor
      setCounselor({
        id: 1,
        name: 'Counselor',
        email: undefined,
        avatar: undefined
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounselor();
  }, [basicInformation?.student_id, userRole]);

  const refetch = () => {
    fetchCounselor();
  };

  return {
    counselor,
    loading,
    error,
    refetch
  };
};
